import '@ant-design/v5-patch-for-react-19';
import * as React from 'react';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { configureStore } from '../shared/store';
import { bindDispatch } from '../shared/store/dispatch';
import { router } from './router';
import { rootSaga } from '../shared/store/rootSaga';
import { registerServiceWorker } from '../shared/utils/pwa';
import { initPwaDeferredInstallPrompt } from '../shared/utils/usePwaDeferredInstallPrompt';
import { bootstrapTheme } from '../shared/utils/theme-preference';

bootstrapTheme();
initPwaDeferredInstallPrompt();

// Ensure document language for client-only shells (axe / WCAG 3.1.1)
if (!document.documentElement.getAttribute('lang')) {
  document.documentElement.setAttribute('lang', 'en');
}

// Trusted Types compatibility shim for third-party libraries (e.g. html2canvas)
if (
  (window as any).trustedTypes &&
  !(window as any).trustedTypes.defaultPolicy
) {
  (window as any).trustedTypes.createPolicy('default', {
    createHTML: (input: string) => input,
    createScript: (input: string) => input,
    createScriptURL: (input: string) => input,
  });
}

registerServiceWorker();


const store =
  window.store ||
  configureStore({
    initialState: window.__PRELOADED_STATE__,
  });

bindDispatch(store.dispatch);

export default store;

/** Cancel a prior root saga when this module re-evaluates (HMR keeps `window.store`). */
const previousSagaTask = window.__ROOT_SAGA_TASK__;
if (previousSagaTask) {
  previousSagaTask.cancel();
}

let sagaTask = store.runSaga(rootSaga);
window.__ROOT_SAGA_TASK__ = sagaTask;

const appTree = (
  <Provider store={store}>
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  </Provider>
);

const appContainer = document.getElementById('app');
if (appContainer) {
  const hasServerMarkup =
    appContainer.hasChildNodes() &&
    Array.from(appContainer.childNodes).some(
      (node) => node.nodeType === Node.ELEMENT_NODE
    );

  if (hasServerMarkup) {
    hydrateRoot(appContainer, appTree);
  } else if (!window.__APP_ROOT__) {
    window.__APP_ROOT__ = createRoot(appContainer);
    window.__APP_ROOT__.render(appTree);
  } else {
    window.__APP_ROOT__.render(appTree);
  }
}

if (process.env.NODE_ENV === 'development') {
  if (module.hot) {
    module.hot.accept();

    module.hot.accept('../shared/store/rootSaga', () => {
      const current = window.__ROOT_SAGA_TASK__ ?? sagaTask;
      current.cancel();
      current.toPromise().then(() => {
        sagaTask = store.runSaga(require('../shared/store/rootSaga').rootSaga);
        window.__ROOT_SAGA_TASK__ = sagaTask;
      });
    });
  }

  if (!window.store) {
    window.store = store;
  }
}
